/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

/**
 * Creates a predicate function that determines if a value is less than a threshold.
 *
 * @template T - The type of the values (number or string)
 * @param threshold - The threshold value to compare against
 * @param value - The value to test
 *
 * @remarks
 * - Pure function with no side effects
 * - Uses JavaScript's `<` operator for comparison
 * - Works with both numbers and strings (lexicographic comparison for strings)
 * - Useful for array filtering and functional composition
 *
 * @example
 * ```typescript
 * const isBelowLimit = isLesser(100);
 * isBelowLimit(50);  // true
 * isBelowLimit(150); // false
 *
 * const isBeforeM = isLesser('M');
 * isBeforeM('A'); // true
 * isBeforeM('Z'); // false
 *
 * // Useful with arrays
 * const temperatures = [15, 25, 35, 45];
 * const cool = temperatures.filter(isLesser(30)); // [15, 25]
 * ```
 */
export const isLesser =
  <T extends number | string>(threshold: T) =>
  (value: T): boolean =>
    value < threshold;
